import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  X,
  Send,
  Sparkles,
  Loader2,
  Trash2,
  Mic,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { isSpeechSynthesisSupported, speakText, stopSpeaking } from '@/lib/speech';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { useAIChatStream } from '@/hooks/useAIChatStream';
import { useActiveEditor } from '@/store/editorContext';
import {
  AGENT_BOOTSTRAP_PROMPT,
  QUICK_ACTIONS,
  UBO_PRIMARY,
  UBO_SECONDARY,
  type AnyEditor,
  type ChatMessage,
  type ChatMode,
} from './ai/aiChatShared';
import {
  extractDocumentContext,
  getBlockIdForPage,
  getTimeGreeting,
  replacePageContent,
  scrollToBlockId,
} from './ai/aiChatEditorUtils';
import { MD_COMPONENTS, ModeToggle } from './ai/AIChatMessageParts';
import { AIChatMessageBubble } from './ai/AIChatMessageBubble';

interface AIChatPanelProps {
  open: boolean;
  onClose: () => void;
  quoteId?: string;
  onCreateBlock?: (
    type: string,
    content: Record<string, unknown>,
    insertAfterPage?: number,
  ) => Promise<{ blockId: string } | null>;
  onDeleteBlock?: (pageNumber: number) => Promise<boolean>;
  onMoveBlock?: (pageNumber: number, newPosition: number) => Promise<boolean>;
  onSyncEditor?: () => void;
}

export function AIChatPanel({ open, onClose, quoteId, onCreateBlock, onDeleteBlock, onMoveBlock, onSyncEditor }: AIChatPanelProps) {
  const { activeEditor: editor } = useActiveEditor();
  const editorRef = useRef<AnyEditor>(null);
  const [input, setInput] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedText, setSelectedText] = useState('');
  const [mode, setMode] = useState<ChatMode>('ask');
  const prevChatModeRef = useRef<ChatMode>('ask');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const inputForSpeechRef = useRef(input);
  inputForSpeechRef.current = input;

  const {
    supported: speechInputSupported,
    isListening: isSpeechListening,
    error: speechInputError,
    toggleListening: toggleSpeechInput,
    stopListening: stopSpeechInput,
    clearError: clearSpeechInputError,
  } = useSpeechRecognition({
    getBaseText: () => inputForSpeechRef.current,
    onTranscript: setInput,
  });

  const [speakingId, setSpeakingId] = useState<string | null>(null);
  const ttsSupported = isSpeechSynthesisSupported();

  const greeting = useMemo(() => getTimeGreeting(), [open]);

  useEffect(() => {
    if (editor) editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    const ed = editor || editorRef.current;
    if (!ed) return;

    const onSelectionUpdate = () => {
      const { from, to } = ed.state.selection;
      setSelectedText(from === to ? '' : ed.state.doc.textBetween(from, to, ' '));
    };

    onSelectionUpdate();
    ed.on('selectionUpdate', onSelectionUpdate);
    ed.on('transaction', onSelectionUpdate);
    return () => {
      ed.off('selectionUpdate', onSelectionUpdate);
      ed.off('transaction', onSelectionUpdate);
    };
  }, [editor]);

  const resolveEditor = useCallback(
    (): AnyEditor | null => editor || editorRef.current,
    [editor],
  );

  const getSelectedText = useCallback((): string => selectedText, [selectedText]);

  const getDocumentContext = useCallback((): string => {
    const ed = resolveEditor();
    return ed ? extractDocumentContext(ed) : '';
  }, [resolveEditor]);

  const clearInput = useCallback(() => setInput(''), []);

  const {
    messages,
    setMessages,
    isStreaming,
    streamingPreview,
    toolResults,
    currentEditPageRef,
    sendMessage,
    stopStreaming,
  } = useAIChatStream({
    quoteId,
    mode,
    resolveEditor,
    getSelectedText,
    getDocumentContext,
    onCreateBlock,
    onDeleteBlock,
    onMoveBlock,
    onSyncEditor,
    stopSpeechInput,
    onSendStart: clearInput,
  });

  useEffect(() => {
    if (!open) {
      stopSpeaking();
      stopSpeechInput();
      setSpeakingId(null);
    }
  }, [open, stopSpeechInput]);

  useEffect(() => {
    if (mode === 'agent' && prevChatModeRef.current === 'ask' && !isStreaming) {
      setInput((prev) => (prev.trim() ? prev : AGENT_BOOTSTRAP_PROMPT));
    }
    prevChatModeRef.current = mode;
  }, [mode, isStreaming]);

  useEffect(() => {
    if (isStreaming) {
      stopSpeaking();
      stopSpeechInput();
    }
  }, [isStreaming, stopSpeechInput]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const viewport = scrollRef.current?.querySelector(
        '[data-radix-scroll-area-viewport]',
      );
      if (viewport) viewport.scrollTop = viewport.scrollHeight;
    });
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = input.trim();
      if (!trimmed || isStreaming) return;
      sendMessage(trimmed);
    },
    [input, isStreaming, sendMessage],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit],
  );

  const handleQuickAction = useCallback(
    (actionId: string) => {
      if (isStreaming) return;
      sendMessage('', actionId);
    },
    [isStreaming, sendMessage],
  );

  const navigateToPage = useCallback(
    (pageNumber: number) => {
      const ed = resolveEditor();
      if (!ed) return;
      const blockId = getBlockIdForPage(ed, pageNumber);
      if (blockId) scrollToBlockId(blockId);
    },
    [resolveEditor],
  );

  const navigateToBlock = useCallback((blockId: string) => {
    scrollToBlockId(blockId);
  }, []);

  const mdComponents = useMemo(
    () => ({
      ...MD_COMPONENTS,
      a: ({ href, children }: any) => {
        const pageMatch = href?.match(/^#page-(\d+)$/);
        if (pageMatch) {
          return (
            <button
              type="button"
              onClick={() => navigateToPage(parseInt(pageMatch[1], 10))}
              className="inline-flex items-center gap-0.5 font-medium underline decoration-1 underline-offset-2 transition-colors hover:opacity-80"
              style={{ color: UBO_PRIMARY }}
            >
              {children}
            </button>
          );
        }
        return (
          <a
            href={href}
            className="underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      },
    }),
    [navigateToPage],
  );

  const handleCopy = useCallback((content: string, msgId: string) => {
    navigator.clipboard.writeText(content);
    setCopiedId(msgId);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  const handleApply = useCallback(
    (msg: ChatMessage) => {
      const ed = resolveEditor();
      if (!ed || !msg.content) return;

      const pageMatch = msg.content.match(/^\[Pagina (\d+)\]\n/);
      if (pageMatch) {
        const pageIdx = parseInt(pageMatch[1], 10);
        const textWithoutHeader = msg.content.replace(/^\[Pagina \d+\]\n/, '');
        replacePageContent(ed, pageIdx, textWithoutHeader);
        return;
      }

      if (msg.targetPageIndex) {
        replacePageContent(ed, msg.targetPageIndex, msg.content);
        return;
      }

      const { from, to } = ed.state.selection;
      if (from !== to) {
        ed.chain().focus().deleteRange({ from, to }).insertContentAt(from, msg.content).run();
      } else {
        ed.chain().focus().insertContent(msg.content).run();
      }
    },
    [resolveEditor],
  );

  const handleStop = useCallback(() => {
    stopStreaming();
    setSpeakingId(null);
  }, [stopStreaming]);

  const handleSpeakToggle = useCallback(
    (msg: ChatMessage) => {
      if (speakingId === msg.id) {
        stopSpeaking();
        setSpeakingId(null);
      } else {
        speakText(msg.content, 'nl-NL', () => setSpeakingId(null));
        setSpeakingId(msg.id);
      }
    },
    [speakingId],
  );

  const handleClearChat = useCallback(() => {
    handleStop();
    setMessages([]);
  }, [handleStop, setMessages]);

  const ed = resolveEditor();
  const pageCount = ed
    ? (() => {
        let c = 0;
        ed.state.doc.forEach(() => {
          c++;
        });
        return c;
      })()
    : 0;

  const isLastAssistant = (msgId: string) =>
    messages[messages.length - 1]?.id === msgId;

  return (
    <div
      className={cn(
        'flex flex-col self-stretch min-h-0 border-l transition-all duration-300 ease-in-out',
        'bg-gradient-to-b from-slate-50/95 to-slate-100/40',
        'border-[color:var(--ubo-chat-border)]',
        open ? 'w-[380px] min-w-[380px]' : 'w-0 min-w-0 overflow-hidden border-l-0',
      )}
      style={{ '--ubo-chat-border': 'rgba(7, 7, 68, 0.12)' } as React.CSSProperties}
    >
      {open && (
        <>
          <header
            className="shrink-0 border-b px-4 py-3"
            style={{ borderColor: `${UBO_SECONDARY}18` }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg shadow-sm"
                    style={{
                      background: `linear-gradient(135deg, ${UBO_PRIMARY} 0%, ${UBO_SECONDARY} 100%)`,
                    }}
                  >
                    <Sparkles className="h-4 w-4 text-white" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <h2
                      className="truncate text-sm font-semibold leading-tight"
                      style={{ color: UBO_SECONDARY }}
                    >
                      AI-assistent
                    </h2>
                    <p className="text-[11px] font-medium text-slate-500">Offerte & tekst</p>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <ModeToggle mode={mode} onChange={setMode} disabled={isStreaming} />
                  {pageCount > 0 && (
                    <span
                      className="inline-flex rounded-md px-2 py-0.5 text-[10px] font-semibold tracking-wide"
                      style={{
                        backgroundColor: `${UBO_SECONDARY}0f`,
                        color: UBO_SECONDARY,
                      }}
                    >
                      {pageCount} {pageCount === 1 ? 'pagina' : "pagina's"}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                {messages.length > 0 && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={handleClearChat}
                    title="Chat wissen"
                    className="text-slate-400 hover:text-slate-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onClose}
                  title="Paneel sluiten"
                  className="text-slate-400 hover:text-slate-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </header>

          {mode === 'ask' && (
            <div
              className="shrink-0 flex flex-wrap gap-1.5 border-b px-4 py-2.5"
              style={{ borderColor: `${UBO_SECONDARY}10` }}
            >
              {QUICK_ACTIONS.map((action) => {
                const Icon = action.Icon;
                return (
                  <button
                    key={action.id}
                    type="button"
                    onClick={() => handleQuickAction(action.id)}
                    disabled={isStreaming}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border border-slate-200/90 bg-white/80 px-2 py-0.5 text-[11px] font-medium text-slate-600 shadow-sm transition-all',
                      'hover:border-[rgb(0,7,236)]/40 hover:bg-white hover:text-[#070744] hover:shadow',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                    )}
                  >
                    <Icon
                      className="h-3 w-3 shrink-0 text-[rgb(0,7,236)] opacity-90"
                      aria-hidden
                    />
                    {action.label}
                  </button>
                );
              })}
            </div>
          )}

          <ScrollArea ref={scrollRef} className="min-h-0 flex-1">
            <div className="flex flex-col gap-3.5 p-4">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center px-1 py-10 text-center">
                  <div
                    className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl shadow-md ring-1 ring-black/5"
                    style={{
                      background: `linear-gradient(145deg, ${UBO_PRIMARY}12 0%, ${UBO_SECONDARY}18 100%)`,
                    }}
                  >
                    <Sparkles className="h-7 w-7" style={{ color: UBO_PRIMARY }} />
                  </div>
                  <p
                    className="text-base font-semibold tracking-tight"
                    style={{ color: UBO_SECONDARY }}
                  >
                    {greeting}
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-600">
                    Hoe kan ik je helpen?
                  </p>
                  <p className="mt-3 max-w-[272px] text-xs leading-relaxed text-slate-500">
                    {mode === 'agent'
                      ? 'Je invoerveld bevat een startopdracht — druk op Enter of pas de tekst aan. De agent gaat direct aan de slag zonder extra "ga door".'
                      : 'Stel een vraag of selecteer tekst voor gerichte acties. Schakel naar Agent modus om het document te laten bewerken.'}
                  </p>
                  {mode === 'agent' && (
                    <Button
                      type="button"
                      className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:opacity-50"
                      style={{ backgroundColor: UBO_PRIMARY }}
                      disabled={isStreaming}
                      onClick={() => sendMessage(AGENT_BOOTSTRAP_PROMPT)}
                    >
                      Start agent
                    </Button>
                  )}
                </div>
              )}

              {messages.map((msg) => (
                <AIChatMessageBubble
                  key={msg.id}
                  msg={msg}
                  isStreaming={isStreaming}
                  isLast={isLastAssistant(msg.id)}
                  mode={mode}
                  mdComponents={mdComponents}
                  copiedId={copiedId}
                  speakingId={speakingId}
                  ttsSupported={ttsSupported}
                  streamingPreview={streamingPreview}
                  currentEditPage={currentEditPageRef.current}
                  toolResults={toolResults}
                  onStop={handleStop}
                  onCopy={handleCopy}
                  onApply={handleApply}
                  onSpeakToggle={handleSpeakToggle}
                  onNavigateToPage={navigateToPage}
                  onNavigateToBlock={navigateToBlock}
                  onSwitchToAgent={() => setMode('agent')}
                />
              ))}
            </div>
          </ScrollArea>

          <form
            onSubmit={handleSubmit}
            className="shrink-0 border-t bg-white/70 p-3 shadow-[0_-4px_24px_-8px_rgba(7,7,68,0.12)] backdrop-blur-sm"
            style={{ borderColor: `${UBO_SECONDARY}14` }}
          >
            {selectedText && (
              <div
                className="mb-2 flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs"
                style={{
                  backgroundColor: `${UBO_PRIMARY}0d`,
                  borderColor: `${UBO_PRIMARY}30`,
                  color: UBO_SECONDARY,
                }}
              >
                <span className="shrink-0 font-semibold">Selectie</span>
                <span className="truncate opacity-90">
                  {selectedText.slice(0, 60)}
                  {selectedText.length > 60 ? '…' : ''}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 rounded-xl border bg-white p-2 shadow-sm ring-1 ring-slate-200/60">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={mode === 'agent' ? 'Geef een opdracht...' : 'Stel een vraag...'}
                disabled={isStreaming}
                rows={1}
                className={cn(
                  'min-h-[40px] max-h-24 flex-1 resize-none rounded-md border-0 bg-transparent px-2 py-2.5 text-sm leading-5',
                  'text-slate-800 placeholder:text-slate-400',
                  'focus:outline-none focus:ring-0',
                  'disabled:opacity-50',
                )}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = `${Math.min(target.scrollHeight, 96)}px`;
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => {
                  clearSpeechInputError();
                  toggleSpeechInput();
                }}
                disabled={isStreaming || !speechInputSupported}
                title={
                  !speechInputSupported
                    ? 'Spraak niet ondersteund in deze browser'
                    : isSpeechListening
                      ? 'Stop dicteren'
                      : 'Dicteren (microfoon)'
                }
                className={cn(
                  'h-9 w-9 shrink-0 rounded-lg',
                  isSpeechListening &&
                    'bg-red-50 text-red-600 ring-2 ring-red-300 ring-offset-1',
                  !speechInputSupported && 'opacity-40',
                )}
              >
                <Mic className="h-4 w-4" />
              </Button>
              <Button
                type="submit"
                size="icon-sm"
                disabled={!input.trim() || isStreaming}
                className="h-9 w-9 shrink-0 rounded-lg text-white shadow-sm hover:opacity-95"
                style={{ backgroundColor: UBO_PRIMARY }}
              >
                {isStreaming ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            {speechInputError && (
              <p className="mt-1.5 text-center text-[10px] text-red-500">{speechInputError}</p>
            )}
            <p className="mt-2 text-center text-[10px] text-slate-400">
              Enter versturen · Shift+Enter nieuwe regel · Mic dicteren
              {ttsSupported ? ' · luidspreker: antwoord voorlezen' : ''}
            </p>
          </form>
        </>
      )}
    </div>
  );
}
